import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, AlertTriangle, Info, RefreshCw, Activity, Lock, Search } from "lucide-react";

const ACTIONS = ["", "login_success", "login_failed", "employee_created", "role_changed", "employee_password_reset", "html_import"];

export default function Security() {
  const [posture, setPosture] = useState(null);
  const [logs, setLogs] = useState([]);
  const [actionFilter, setActionFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([
        api.get("/security/posture"),
        api.get("/security/audit-logs", { params: { limit: 200, action: actionFilter || undefined }}),
      ]);
      setPosture(p.data); setLogs(l.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [actionFilter]);

  if (!posture) return <div className="p-10 text-slate-500">Loading…</div>;
  const filtered = logs.filter(r => !q || (r.actor_email || "").toLowerCase().includes(q.toLowerCase()) || (r.action || "").includes(q.toLowerCase()) || (r.target || "").toLowerCase().includes(q.toLowerCase()));
  const score = posture.score;
  const scoreColor = score >= 80 ? "text-green-600" : score >= 60 ? "text-amber-600" : "text-red-600";

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-6xl">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Compliance</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Security Center</h1>
          <p className="text-slate-500 mt-1">ISO 27001-aligned controls, audit trail, and live security posture.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="security-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`}/>Refresh
        </Button>
      </header>

      {/* Score + KPIs */}
      <div className="grid lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-1 border-slate-200 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-700 text-white overflow-hidden">
          <CardContent className="p-6">
            <ShieldCheck className="h-8 w-8 mb-2"/>
            <div className="font-display font-black text-5xl">{score}<span className="text-2xl text-white/70">%</span></div>
            <div className="text-xs uppercase tracking-[0.18em] text-white/80 font-bold mt-1">Compliance Score</div>
            <div className="text-[11px] text-white/70 mt-2">ISO 27001 / SOC 2 aligned. Not a certification — operator must complete formal audit.</div>
          </CardContent>
        </Card>
        <Kpi icon={Activity} label="Audit Events" value={posture.stats.total_audit_events} sub={`${posture.stats.events_24h} in last 24h`} color="emerald"/>
        <Kpi icon={AlertTriangle} label="Failed Logins (24h)" value={posture.stats.failed_logins_24h} sub="auto-lockout enabled" color="amber"/>
        <Kpi icon={Lock} label="Locked Accounts" value={posture.stats.locked_accounts} sub="15 min auto-unlock" color="rose"/>
      </div>

      {/* Controls */}
      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg mb-1">Control Posture</h3>
          <p className="text-sm text-slate-500 mb-4">Mapped to ISO/IEC 27001:2022 Annex A controls.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {posture.controls.map(c => (
              <div key={c.id} className="rounded-xl border border-slate-200 p-4 flex gap-3" data-testid={`control-${c.id}`}>
                <div className={`h-9 w-9 flex-shrink-0 rounded-lg grid place-items-center ${c.status === "ok" ? "bg-green-100 text-green-700" : c.status === "warn" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>
                  {c.status === "ok" ? <ShieldCheck className="h-5 w-5"/> : c.status === "warn" ? <AlertTriangle className="h-5 w-5"/> : <Info className="h-5 w-5"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <Badge variant="secondary" className="font-mono bg-slate-100 text-slate-600">{c.id}</Badge>
                    <div className="font-bold text-sm">{c.name}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1.5 leading-snug">{c.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
            <div>
              <h3 className="font-display font-bold text-lg">Audit Log</h3>
              <p className="text-xs text-slate-500">Most recent 200 events. Login attempts, role changes, employee password resets, HTML imports.</p>
            </div>
            <div className="flex gap-2 items-end">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">Action</div>
                <Select value={actionFilter || "__all__"} onValueChange={(v)=>setActionFilter(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="w-44" data-testid="audit-action-filter"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All actions</SelectItem>
                    {ACTIONS.filter(Boolean).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
                <Input placeholder="Search actor or target…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9 w-60" data-testid="audit-search"/>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr><th className="py-2 px-3">When</th><th className="py-2 px-3">Actor</th><th className="py-2 px-3">Action</th><th className="py-2 px-3">Target</th><th className="py-2 px-3">IP / Details</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No audit events.</td></tr>}
                {filtered.map(r => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 px-3 text-xs text-slate-500 whitespace-nowrap">{new Date(r.ts).toLocaleString()}</td>
                    <td className="py-2 px-3">
                      <div className="font-medium">{r.actor_name || "—"}</div>
                      <div className="text-xs text-slate-400">{r.actor_email || r.actor_id}</div>
                    </td>
                    <td className="py-2 px-3">
                      <Badge variant="secondary" className={r.action === "login_failed" ? "bg-red-100 text-red-700" : r.action === "login_success" ? "bg-green-100 text-green-700" : "bg-sky-100 text-sky-700"}>{r.action}</Badge>
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">{r.target}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{r.ip || "—"} {r.details && Object.keys(r.details).length ? `· ${JSON.stringify(r.details)}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color }) {
  const map = {
    sky: "bg-sky-50 text-sky-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
  };
  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-5">
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${map[color]}`}><Icon className="h-5 w-5"/></div>
        <div className="mt-3 font-display font-black text-3xl text-slate-900">{value}</div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
        <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
      </CardContent>
    </Card>
  );
}
